/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createContext, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { ErrorComponent } from 'components';
import { useMixpanel, useSession } from 'context';
import { useLocalStorage, useWillMount } from 'hooks';
import { getReceiveOrderDetails } from 'onramp-pages/api';
import { MoneyScaled } from 'types';
import { PaymentMethod } from 'types/piqPaymentMethod';
import { inIFrame } from 'utils';

const LOCAL_STORAGE_KEY = 'qb_web_static_topUpData';
const EDITABLE_LOCAL_STORAGE_KEY = 'qb_web_editable_topUpData';

interface EditableOnrampData {
  buyPreviewId?: string;
  autoProcess?: boolean;
  acceptedTerms?: boolean;
}

type StaticOnrampData = {
  receiveOrderPreviewId: string;
  existingUser: boolean;
  fiatAmount: MoneyScaled;
  cryptoAmount: MoneyScaled;
  address: string;
  email?: string;
  redirectUrl?: string;
  failureRedirectUrl?: string;
  hasCardData?: boolean;
  merchantName?: string;
  merchantId?: string;
  logoUrl?: string;
  logoEnabled?: boolean;
  paymentMethod?: PaymentMethod;
};

export type OnrampData = StaticOnrampData & EditableOnrampData;

type OnrampProviderValue = OnrampData & {
  setOnrampData: (topUpData: Partial<OnrampData>) => void;
};

const placeholderDefaults = {
  address: '',
  fiatAmount: { amount: 0, currencyCode: 'EUR', scale: 0 } as MoneyScaled,
  cryptoAmount: { amount: 0, currencyCode: 'BTC', scale: 0 } as MoneyScaled,
};

const OnrampContext = createContext<OnrampProviderValue>({
  receiveOrderPreviewId: '',
  existingUser: false,
  ...placeholderDefaults,
  setOnrampData: () => undefined,
});

export const useSetOnrampData = (): ((data: Partial<OnrampData>) => void) =>
  useContext(OnrampContext).setOnrampData;

export const useOnrampData: () => OnrampData = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setOnrampData: setOnrampData, ...rest } = useContext(OnrampContext);
  return rest;
};

export const OnrampProvider = ({ children }: { children: JSX.Element }) => {
  const session = useSession();
  const { mixpanelTrackEvent, mixpanel } = useMixpanel({
    Product: 'TopUp',
    Page: 'Provider',
  });
  const [params] = useSearchParams();
  const staticDataFromParams: StaticOnrampData = {
    receiveOrderPreviewId: params.get('receiveOrderPreviewId') || '',
    email: params.get('email') || undefined,
    existingUser: params.get('existingUser') === 'true',
    hasCardData: params.get('hasCardData') === 'true',
    ...placeholderDefaults,
  };

  const [staticOnrampData, setStaticOnrampData] =
    useLocalStorage<StaticOnrampData>(LOCAL_STORAGE_KEY, staticDataFromParams);
  const [editableOnrampData, setEditableOnrampData] =
    useLocalStorage<EditableOnrampData>(EDITABLE_LOCAL_STORAGE_KEY, {});

  const [{ error }, resetOnrampData] = useAsyncFn(async () => {
    setStaticOnrampData({
      ...staticDataFromParams,
    });
    setEditableOnrampData({});
    const orderData = await getReceiveOrderDetails(
      staticDataFromParams.receiveOrderPreviewId
    );
    setStaticOnrampData((prevData) => ({
      ...prevData!,
      merchantName: orderData.merchant.displayName,
      merchantId: orderData.merchant.uuid,
      fiatAmount: orderData.amount,
      redirectUrl: orderData.redirectUrl,
      failureRedirectUrl: orderData.failureRedirectUrl,
      logoUrl: orderData.logoUrl,
      logoEnabled: orderData.logoEnabled,
      paymentMethod: orderData.paymentMethod,
      address: orderData.address,
      cryptoAmount: orderData.cryptoAmount,
    }));
    mixpanel.register({
      merchantName: orderData.merchant.displayName,
      paymentMethod: orderData.paymentMethod ?? undefined,
    });
  }, [setStaticOnrampData, staticDataFromParams]);

  useWillMount(() => {
    const paramReceiveId = staticDataFromParams.receiveOrderPreviewId;
    if (paramReceiveId) {
      // When receiveOrderPreviewId is present in the URL, we want to reset the onramp data
      void resetOnrampData();
      mixpanel.register({
        receiveOrderPreviewId: paramReceiveId,
      });
    }
    if (staticDataFromParams.email) {
      mixpanel.alias(staticDataFromParams.email);
    }
    mixpanelTrackEvent('Topup started', {
      description: 'User has landed on the Topup page',
      wasLoggedIn: !!session,
      autoLogin: window.location.pathname.includes('login'),
      isIframe: inIFrame(),
    });
  });

  const value = useMemo(
    () => ({
      ...staticOnrampData!,
      ...editableOnrampData,
      setOnrampData: (data: Partial<EditableOnrampData>) =>
        setEditableOnrampData((prevState) => ({ ...prevState, ...data })),
    }),
    [staticOnrampData, editableOnrampData, setEditableOnrampData]
  );

  if (error) return <ErrorComponent error={error}></ErrorComponent>;

  return (
    <OnrampContext.Provider value={value}>{children}</OnrampContext.Provider>
  );
};
